import React from 'react'
import AdminBar from '../components/AdminBar.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import { getSession } from '../lib/auth'
import { subscribeSysConfigFS, saveSysConfigFS } from '../services/firestore/sysConfigService'
import { app as firebaseApp } from '@/firebase'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot
} from 'firebase/firestore'

function createEmptyAnnouncement() {
  return {
    subject: '',
    headline: '',
    message: '',
  }
}

function fmtTimestamp(value) {
  if (!value) return '—'
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleString()
  }
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000).toLocaleString()
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString()
}

export default function AdminHome() {
  const sess = getSession()
  const db = React.useMemo(() => getFirestore(firebaseApp), [])
  const hydratedFormRef = React.useRef(false)
  const [cfg, setCfg] = React.useState(null)
  const [form, setForm] = React.useState(() => createEmptyAnnouncement())
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState('')
  const [err, setErr] = React.useState('')
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [history, setHistory] = React.useState([])
  const [openHistoryId, setOpenHistoryId] = React.useState('')

  React.useEffect(() => {
    return subscribeSysConfigFS((next) => {
      setCfg(next || {})
      if (!hydratedFormRef.current) {
        setForm(createEmptyAnnouncement())
        hydratedFormRef.current = true
      }
    })
  }, [])

  React.useEffect(() => {
    const q = query(
      collection(db, 'announcementSends'),
      orderBy('sentAt', 'desc'),
      limit(10)
    )
    return onSnapshot(q, (snap) => {
      setHistory(snap.docs.map((doc) => ({ id: doc.id, ...(doc.data() || {}) })))
    })
  }, [db])

  const announcementMeta = cfg?.announcement || {}
  const canSend =
    String(form.subject || '').trim() &&
    String(form.headline || '').trim() &&
    String(form.message || '').trim()

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setMsg('')
    setErr('')
  }

  function resetForm() {
    setForm(createEmptyAnnouncement())
  }

  async function sendAnnouncement() {
    if (saving) return
    const subject = String(form.subject || '').trim()
    const headline = String(form.headline || '').trim()
    const message = String(form.message || '').trim()

    if (!subject || !headline || !message) {
      setErr('Subject, headline, and message are required before sending.')
      setConfirmOpen(false)
      return
    }

    setSaving(true)
    setErr('')
    setMsg('')

    try {
      const sendVersion = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      await saveSysConfigFS(
        {
          ...(cfg || {}),
          announcement: {
            ...(cfg?.announcement || {}),
            subject,
            headline,
            message,
            sendVersion,
            requestedAt: new Date().toISOString(),
            requestedByEmail: sess?.email || '',
            requestedByName: sess?.displayName || sess?.email || 'Admin',
            lastStatus: 'sending',
            lastError: '',
          }
        },
        {
          email: sess?.email || '',
          displayName: sess?.displayName || sess?.email || 'Admin',
        }
      )
      resetForm()
      setMsg('Announcement queued. Emails are being sent to all active users.')
    } catch (error) {
      setErr(error?.message || 'Failed to send announcement.')
    } finally {
      setSaving(false)
      setConfirmOpen(false)
    }
  }

  return (
    <div>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, marginBottom: 12 }}>Communications</h2>
      <AdminBar title="" />

      <div className="card admin-card" style={{ marginTop: 16 }}>
        <div className="admin-head">
          <div>
            <h3 style={{ margin: 0 }}>Announcement Email</h3>
            <div className="muted" style={{ marginTop: 4 }}>
              Sends a MaintainIQ announcement email to all active users.
            </div>
          </div>
        </div>

        <div className="announcement-grid">
          <div className="field-with-error">
            <label htmlFor="announcement-subject">Email Subject</label>
            <input
              id="announcement-subject"
              value={form.subject}
              onChange={(e) => setField('subject', e.target.value)}
              placeholder="MaintainIQ Scheduled Maintenance"
            />
          </div>

          <div className="field-with-error">
            <label htmlFor="announcement-headline">Email Headline</label>
            <input
              id="announcement-headline"
              value={form.headline}
              onChange={(e) => setField('headline', e.target.value)}
              placeholder="Scheduled maintenance tonight"
            />
          </div>
        </div>

        <div className="field-with-error" style={{ marginTop: 12 }}>
          <label htmlFor="announcement-message">Message</label>
          <textarea
            id="announcement-message"
            rows={6}
            value={form.message}
            onChange={(e) => setField('message', e.target.value)}
            placeholder="Let users know what is changing, when it matters, and what they need to do."
          />
        </div>

        {err ? <div className="error-text" style={{ minHeight: 0, marginTop: 8 }}>{err}</div> : null}
        {msg ? <div className="ok" style={{ marginTop: 10 }}>{msg}</div> : null}

        <div className="announcement-actions">
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={!canSend || saving}
            data-tooltip={!canSend ? 'Subject, headline, and message are required.' : undefined}
          >
            {saving ? 'Sending…' : 'Send Announcement Email'}
          </button>
        </div>

        <div className="announcement-meta">
          <div><strong>Last Status:</strong> {announcementMeta.lastStatus || '—'}</div>
          <div><strong>Last Sent:</strong> {fmtTimestamp(announcementMeta.lastSentAt)}</div>
          <div><strong>Last Subject:</strong> {announcementMeta.lastSubject || '—'}</div>
          <div><strong>Recipients:</strong> {announcementMeta.lastRecipientCount ?? '—'}</div>
          {announcementMeta.lastError ? (
            <div className="error-text" style={{ minHeight: 0 }}>
              {announcementMeta.lastError}
            </div>
          ) : null}
        </div>
      </div>

      <div className="card admin-card" style={{ marginTop: 16 }}>
        <div className="admin-head">
          <div>
            <h3 style={{ margin: 0 }}>Announcement History</h3>
            <div className="muted" style={{ marginTop: 4 }}>
              Recent system-wide communications sent from MaintainIQ.
            </div>
          </div>
        </div>

        {!history.length ? (
          <div className="muted">No announcement emails have been logged yet.</div>
        ) : (
          <div className="announcement-history">
            {history.map((item) => (
              <div key={item.id} className="announcement-history-item">
                <button
                  type="button"
                  className="announcement-history-toggle"
                  onClick={() => setOpenHistoryId((prev) => (prev === item.id ? '' : item.id))}
                  aria-expanded={openHistoryId === item.id}
                >
                  <div className="announcement-history-top">
                    <div>
                      <strong>{item.subject || '—'}</strong>
                      <div className="muted" style={{ marginTop: 4 }}>
                        {item.headline || '—'}
                      </div>
                    </div>
                    <div className="announcement-history-badges">
                      <span className={`pill ${String(item.status || '').startsWith('sent') ? 'ok' : ''}`}>
                        {item.status || '—'}
                      </span>
                      <span className="muted">{openHistoryId === item.id ? 'Hide' : 'Show'}</span>
                    </div>
                  </div>
                </button>
                {openHistoryId === item.id ? (
                  <>
                    <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                      {item.message || '—'}
                    </div>
                    <div className="announcement-history-meta">
                      <span><strong>Sent:</strong> {fmtTimestamp(item.sentAt)}</span>
                      <span><strong>By:</strong> {item.requestedByName || item.requestedByEmail || '—'}</span>
                      <span><strong>Recipients:</strong> {item.recipientCount ?? 0}</span>
                    </div>
                    {Array.isArray(item.recipientsSample) && item.recipientsSample.length ? (
                      <div className="muted" style={{ marginTop: 6 }}>
                        Sample recipients: {item.recipientsSample.join(', ')}
                      </div>
                    ) : null}
                    {item.error ? (
                      <div className="error-text" style={{ minHeight: 0, marginTop: 6 }}>{item.error}</div>
                    ) : null}
                  </>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Send Announcement"
        tone="default"
        confirmLabel={saving ? 'Sending…' : 'Send Email'}
        onCancel={() => { if (!saving) setConfirmOpen(false) }}
        onConfirm={sendAnnouncement}
        message={
          <div>
            <p style={{ marginTop: 0 }}>
              This will send an announcement email to all active users.
            </p>
            <p style={{ marginBottom: 0 }}>
              <strong>Subject:</strong> {form.subject || '—'}
            </p>
          </div>
        }
      />
    </div>
  )
}
